import styled from "@emotion/styled/macro";
import { css } from "@emotion/react";

/* ─── Status colors ─── */
const statusColors = {
    resulted: { bg: "#e8f5e9", border: "#43a047", text: "#2e7d32", accent: "#43a047" },
    pending:  { bg: "#fff8e1", border: "#ffa000", text: "#e65100", accent: "#ffa000" },
    not_ordered: { bg: "#f5f5f5", border: "#bdbdbd", text: "#757575", accent: "#9e9e9e" },
    abnormal: { bg: "#ffebee", border: "#e53935", text: "#b71c1c", accent: "#e53935" },
};

/* ─── Page Wrapper ─── */
export const InvestPageWrapper = styled.div`
    padding: 0 20px;
    width: 100%;
    @media (max-width: 768px) { padding: 0 12px; }
`;

/* ─── Header Bar ─── */
export const InvestHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px;
    background: linear-gradient(135deg, #00695c 0%, #004d40 100%);
    color: #fff;
    border-radius: 10px;
    margin-bottom: 20px;
    flex-wrap: wrap;
    gap: 12px;
    box-shadow: 0 4px 12px rgba(0,105,92,0.25);

    @media (max-width: 768px) {
        flex-direction: column;
        align-items: flex-start;
        padding: 14px 16px;
    }
`;

export const InvestHeaderTitle = styled.div`
    h3 { margin: 0 0 4px 0; font-size: 1.15rem; font-weight: 600; letter-spacing: 0.3px; }
    p { margin: 0; font-size: 0.82rem; opacity: 0.85; }
`;

export const InvestHeaderStats = styled.div`
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
    @media (max-width: 768px) { gap: 8px; width: 100%; }
`;

export const StatBadge = styled.div`
    display: flex;
    align-items: center;
    gap: 5px;
    padding: 5px 12px;
    background: rgba(255,255,255,0.15);
    border-radius: 18px;
    font-size: 0.78rem;
    font-weight: 500;
    backdrop-filter: blur(4px);
    span.count { font-weight: 700; font-size: 0.95rem; }
`;

/* ─── Toolbar row ─── */
export const Toolbar = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 10px;
    margin-bottom: 16px;
`;

export const FilterTabs = styled.div`
    display: flex;
    gap: 4px;
    background: #f0f2f5;
    border-radius: 8px;
    padding: 3px;
    flex-wrap: wrap;
`;

export const FilterTab = styled.button`
    padding: 6px 14px;
    border: none;
    border-radius: 6px;
    font-size: 0.78rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
    white-space: nowrap;
    background: ${function(p) { return p.active ? "#00695c" : "transparent"; }};
    color: ${function(p) { return p.active ? "#fff" : "#546e7a"; }};
    &:hover { background: ${function(p) { return p.active ? "#00695c" : "#e0e0e0"; }}; }
`;

export const QuickOrderBtn = styled.button`
    padding: 8px 18px;
    background: #00695c;
    color: #fff;
    border: none;
    border-radius: 6px;
    font-size: 0.82rem;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.2s;
    display: flex;
    align-items: center;
    gap: 6px;
    white-space: nowrap;
    &:hover { background: #004d40; }
    &:disabled { background: #80cbc4; cursor: not-allowed; }
`;

/* ─── Investigation Card Grid ─── */
export const InvestGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 14px;
    margin-bottom: 24px;

    @media (max-width: 1100px) { grid-template-columns: repeat(2, 1fr); }
    @media (max-width: 600px) { grid-template-columns: 1fr; }
`;

export const InvestCard = styled.div`
    position: relative;
    background: ${function(p) {
        if (p.abnormal) return statusColors.abnormal.bg;
        const s = p.status || "not_ordered";
        return (statusColors[s] || statusColors.not_ordered).bg;
    }};
    border: 1.5px solid ${function(p) {
        if (p.abnormal) return statusColors.abnormal.border;
        const s = p.status || "not_ordered";
        return (statusColors[s] || statusColors.not_ordered).border;
    }};
    border-radius: 10px;
    padding: 16px;
    transition: transform 0.2s, box-shadow 0.2s;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 6px 18px rgba(0,0,0,0.07);
    }

    ${function(p) { return p.isDue ? css`
        &::before {
            content: "DUE NOW";
            position: absolute;
            top: -8px;
            right: 12px;
            padding: 2px 8px;
            background: #ff6f00;
            color: #fff;
            font-size: 0.6rem;
            font-weight: 700;
            border-radius: 4px;
            letter-spacing: 0.8px;
        }
    ` : ""; }}
`;

export const CardTop = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: 10px;
    gap: 8px;
`;

export const CardIcon = styled.div`
    width: 34px;
    height: 34px;
    border-radius: 8px;
    background: ${function(p) {
        if (p.abnormal) return statusColors.abnormal.accent;
        const s = p.status || "not_ordered";
        return (statusColors[s] || statusColors.not_ordered).accent;
    }};
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.9rem;
    flex-shrink: 0;
`;

export const CardTitle = styled.div`
    flex: 1;
    .card-name { font-size: 0.88rem; font-weight: 600; color: #263238; margin-bottom: 2px; }
    .card-specimen { font-size: 0.72rem; color: #78909c; }
`;

export const StatusPill = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 3px;
    padding: 3px 8px;
    border-radius: 10px;
    font-size: 0.68rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    white-space: nowrap;
    background: ${function(p) {
        if (p.status === "resulted") return "#e8f5e9";
        if (p.status === "pending") return "#fff8e1";
        return "#f5f5f5";
    }};
    color: ${function(p) {
        if (p.status === "resulted") return "#2e7d32";
        if (p.status === "pending") return "#e65100";
        return "#757575";
    }};
`;

export const CardBody = styled.div`
    .result-row {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-bottom: 6px;
        flex-wrap: wrap;
    }
    .result-label { font-size: 0.75rem; color: #78909c; }
    .result-value {
        font-size: 0.88rem;
        font-weight: 600;
        color: ${function(p) { return p.abnormal ? "#b71c1c" : "#263238"; }};
    }
    .result-date { font-size: 0.72rem; color: #90a4ae; }
    .abnormal-flag {
        display: inline-flex;
        align-items: center;
        gap: 3px;
        padding: 2px 6px;
        background: #ffcdd2;
        color: #b71c1c;
        border-radius: 4px;
        font-size: 0.68rem;
        font-weight: 600;
    }
    .recommended-text {
        font-size: 0.7rem;
        color: #78909c;
        margin-top: 4px;
    }
`;

export const CardActions = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 6px;
    margin-top: 10px;
`;

export const SmallBtn = styled.button`
    padding: 4px 10px;
    border: 1px solid ${function(p) { return p.color || "#78909c"; }};
    background: transparent;
    color: ${function(p) { return p.color || "#546e7a"; }};
    border-radius: 5px;
    font-size: 0.72rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    white-space: nowrap;
    &:hover { background: ${function(p) { return p.color || "#78909c"; }}; color: #fff; }
`;

/* ─── Timeline/Table View ─── */
export const InvestTable = styled.div`
    background: #fff;
    border-radius: 10px;
    border: 1px solid #e8edf2;
    overflow: hidden;
    margin-bottom: 24px;
`;

export const TableHeader = styled.div`
    display: grid;
    grid-template-columns: 2fr 1fr 1fr 1.5fr 1fr 1fr;
    padding: 10px 16px;
    background: #f5f7fa;
    border-bottom: 1px solid #e8edf2;
    font-size: 0.72rem;
    font-weight: 600;
    color: #78909c;
    text-transform: uppercase;
    letter-spacing: 0.5px;

    @media (max-width: 768px) {
        display: none;
    }
`;

export const TableRow = styled.div`
    display: grid;
    grid-template-columns: 2fr 1fr 1fr 1.5fr 1fr 1fr;
    padding: 12px 16px;
    border-bottom: 1px solid #f0f2f5;
    align-items: center;
    font-size: 0.84rem;
    transition: background 0.15s;

    &:hover { background: #fafbfc; }
    &:last-child { border-bottom: none; }

    .row-test { font-weight: 500; color: #263238; }
    .row-date { color: #546e7a; font-size: 0.8rem; }
    .row-result {
        font-weight: 600;
        color: ${function(p) { return p.abnormal ? "#b71c1c" : "#263238"; }};
    }

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
        gap: 4px;
        padding: 10px 14px;
    }
`;

/* ─── Dialog / Modal ─── */
export const DialogOverlay = styled.div`
    position: fixed;
    top: 0; left: 0; right: 0; bottom: 0;
    background: rgba(0,0,0,0.45);
    z-index: 1200;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
`;

export const DialogBox = styled.div`
    background: #fff;
    border-radius: 12px;
    width: 100%;
    max-width: 500px;
    box-shadow: 0 20px 60px rgba(0,0,0,0.15);
    overflow: hidden;
    max-height: 90vh;
    overflow-y: auto;
`;

export const DialogHeader = styled.div`
    padding: 18px 24px;
    background: #f5f7fa;
    border-bottom: 1px solid #e8edf2;
    display: flex;
    align-items: center;
    justify-content: space-between;
    position: sticky;
    top: 0;
    z-index: 1;
    h4 { margin: 0; font-size: 1rem; font-weight: 600; color: #263238; }
`;

export const DialogBody = styled.div`
    padding: 24px;
`;

export const DialogFooter = styled.div`
    padding: 16px 24px;
    border-top: 1px solid #e8edf2;
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    position: sticky;
    bottom: 0;
    background: #fff;
`;

export const DialogCloseBtn = styled.button`
    background: none;
    border: none;
    font-size: 1.2rem;
    cursor: pointer;
    color: #90a4ae;
    padding: 0;
    line-height: 1;
    &:hover { color: #546e7a; }
`;

export const DialogBtn = styled.button`
    padding: 8px 20px;
    border-radius: 6px;
    font-size: 0.85rem;
    font-weight: 600;
    cursor: pointer;
    border: none;
    transition: all 0.2s;
    ${function(p) {
        if (p.variant === "primary") {
            return css`
                background: #00695c;
                color: #fff;
                &:hover { background: #004d40; }
                &:disabled { background: #80cbc4; cursor: not-allowed; }
            `;
        }
        return css`
            background: #f0f2f5;
            color: #546e7a;
            &:hover { background: #e0e0e0; }
        `;
    }}
`;

/* ─── Form field ─── */
export const FormField = styled.div`
    margin-bottom: 16px;
    label {
        display: block;
        font-size: 0.78rem;
        font-weight: 600;
        color: #546e7a;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        margin-bottom: 6px;
    }
    input, select, textarea {
        width: 100%;
        padding: 10px 12px;
        border: 1px solid #cfd8dc;
        border-radius: 6px;
        font-size: 0.88rem;
        color: #263238;
        outline: none;
        transition: border 0.2s;
        &:focus { border-color: #00695c; box-shadow: 0 0 0 2px rgba(0,105,92,0.12); }
    }
    textarea { resize: vertical; min-height: 60px; }
    select { appearance: auto; background: #fff; }
`;

/* ─── Info display ─── */
export const InfoText = styled.div`
    padding: 10px 14px;
    background: #f5f7fa;
    border-radius: 6px;
    font-size: 0.88rem;
    color: #263238;
`;

/* ─── Empty/Loading ─── */
export const EmptyState = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 20px;
    text-align: center;
    color: #90a4ae;
    .empty-icon { font-size: 3rem; margin-bottom: 16px; }
    .empty-title { font-size: 1.05rem; font-weight: 600; color: #546e7a; margin-bottom: 8px; }
    .empty-desc { font-size: 0.85rem; max-width: 380px; line-height: 1.5; }
`;

export const LoadingWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 60px 20px;
    color: #90a4ae;
    font-size: 0.9rem;
    gap: 10px;
`;

export const GoToBookingBtn = styled.button`
    margin-top: 20px;
    padding: 10px 24px;
    background: #00695c;
    color: #fff;
    border: none;
    border-radius: 8px;
    font-size: 0.9rem;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.2s;
    &:hover { background: #004d40; }
`;

/* ─── View toggle ─── */
export const ViewToggle = styled.div`
    display: inline-flex;
    background: #f0f2f5;
    border-radius: 8px;
    padding: 3px;
    gap: 2px;
`;

export const ViewToggleBtn = styled.button`
    padding: 7px 16px;
    border: none;
    border-radius: 6px;
    font-size: 0.8rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
    background: ${function(p) { return p.active ? "#00695c" : "transparent"; }};
    color: ${function(p) { return p.active ? "#fff" : "#546e7a"; }};
    &:hover { background: ${function(p) { return p.active ? "#00695c" : "#e0e0e0"; }}; }
`;

/* ─── Completion bar ─── */
export const CompletionBar = styled.div`
    margin-bottom: 20px;
    background: #fff;
    border-radius: 10px;
    padding: 14px 20px;
    box-shadow: 0 1px 4px rgba(0,0,0,0.06);
    border: 1px solid #e8edf2;
`;

export const CompletionLabel = styled.div`
    display: flex;
    justify-content: space-between;
    margin-bottom: 8px;
    font-size: 0.82rem;
    color: #546e7a;
    span.percent { font-weight: 600; color: #00695c; }
`;

export const BarOuter = styled.div`
    width: 100%;
    height: 8px;
    background: #e8edf2;
    border-radius: 4px;
    overflow: hidden;
`;

export const BarInner = styled.div`
    height: 100%;
    background: linear-gradient(90deg, #00695c, #26a69a);
    border-radius: 4px;
    transition: width 0.6s ease;
    width: ${function(p) { return p.percent || 0; }}%;
`;
